import { useState, useEffect } from "react";
import { BookOpen, Users, Target, Swords, Plus, Trash2, Sparkles, Loader2, Globe } from "lucide-react";
import * as api from "../../lib/api";
import { Card, Field, Label, Hint, SettingsForm, ResultBanner, PRIMARY, useSettings } from "./shared";

const rowInputCls = "w-full px-2 py-1.5 text-xs border border-zinc-100 bg-white focus:outline-none focus:ring-2 placeholder-zinc-400 rounded-md";
const ring = { "--tw-ring-color": PRIMARY };

// Repeatable row editor for buyer_personas, key_pages and competitors.
function ListEditor({ list, blank, empty, renderRow }) {
  const { form, setForm } = useSettings();
  const items = form[list];

  const update = (i, field, value) =>
    setForm(f => ({ ...f, [list]: f[list].map((item, idx) => idx === i ? { ...item, [field]: value } : item) }));
  const add = () => setForm(f => ({ ...f, [list]: [...f[list], blank] }));
  const remove = i => setForm(f => ({ ...f, [list]: f[list].filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-2">
      {items.length === 0
        ? <p className="text-[10px] text-zinc-400">{empty}</p>
        : items.map((item, i) => (
          <div key={i} className="flex gap-1.5 items-start bg-zinc-50 border border-zinc-100 rounded-lg p-2">
            <div className="flex-1 min-w-0">{renderRow(item, (field, value) => update(i, field, value))}</div>
            <button type="button" onClick={() => remove(i)}
              className="flex-shrink-0 p-1.5 text-zinc-400 hover:text-red-500 transition-colors" title="Remove"
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
          </div>
        ))}
      <button type="button" onClick={add}
        className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <Plus size={11} strokeWidth={2.5} /> Add
      </button>
    </div>
  );
}

// A usable website: http(s) with a dotted host. Same check the backend's
// normalize_base_url effectively applies before reading the site.
export function isWebsiteUrl(value) {
  try {
    const u = new URL(/^https?:\/\//i.test(value || "") ? value : `https://${value}`);
    return u.hostname.includes(".");
  } catch {
    return false;
  }
}

// Fills only empty fields from the drafted context, so nothing the user wrote is overwritten.
// The draft isn't saved until the user reviews it and clicks Save.
function DraftFromWebsite() {
  const { form, setForm } = useSettings();
  const [drafting, setDrafting] = useState(false);
  const [result, setResult] = useState(null);
  const hasUrl = isWebsiteUrl(form.astro_site_base_url);

  const draft = async () => {
    setDrafting(true);
    setResult(null);
    try {
      const d = await api.draftBusinessContext(form.astro_site_base_url);
      const updates = {};
      const filled = [];
      const fill = (key, value, label) => {
        const empty = Array.isArray(form[key]) ? form[key].length === 0 : !String(form[key] || "").trim();
        if (empty && (Array.isArray(value) ? value.length : value)) {
          updates[key] = value;
          filled.push(label);
        }
      };
      fill("business_context", d.what_we_do, "what we do");
      fill("article_objective", d.objective, "objective");
      fill("buyer_personas", d.personas, "personas");
      fill("key_pages", d.key_pages, "key pages");
      fill("competitors", d.competitors, "competitors");
      setForm(f => ({ ...f, ...updates }));
      const pages = d.sources?.pages?.length || 0;
      setResult({
        success: true,
        message: filled.length
          ? `Drafted ${filled.join(", ")} from ${pages} page${pages === 1 ? "" : "s"} on ${d.sources?.site_url}. Review, then Save.`
          : "Every field already has content — nothing was replaced. Clear a field to redraft it.",
      });
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message || "Draft failed" });
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="space-y-2">
      <button type="button" onClick={draft} disabled={drafting || !hasUrl}
        title={hasUrl ? undefined : "Add your website URL first"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-zinc-200 rounded-lg text-zinc-700 hover:text-zinc-900 hover:border-zinc-300 disabled:opacity-50 transition-colors"
      >
        {drafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={2} />}
        {drafting ? "Reading your site…" : "Draft from website"}
      </button>
      <Hint>Reads your homepage and top Search Console pages, and finds who ranks next to you. Only empty fields are filled.</Hint>
      <ResultBanner result={result} testId="business-draft-result" />
    </div>
  );
}

// Market the site sells to. Saved with the form; "Detect" proposes the country with the
// most Search Console impressions. AI citation checks run in this country's Google.
function TargetCountryField() {
  const { form, setForm } = useSettings();
  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(false);
  const [detected, setDetected] = useState(null);
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    api.getTargetCountry()
      .then(d => setOptions(d.options || []))
      .catch(err => {
        // Usually a backend without /settings/target-country yet (not redeployed).
        console.error("[Settings] target country options failed:", { status: err.response?.status, message: err.message });
        setOptionsError(true);
      });
  }, []);

  const detect = async () => {
    setDetecting(true);
    try {
      const d = await api.getTargetCountry(true);
      setDetected(d.detected);
      if (d.detected?.code && !form.target_country) setForm(f => ({ ...f, target_country: d.detected.code }));
    } catch {
      setDetected({ code: null, breakdown: [] });
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div>
      <Label>Primary target country</Label>
      <div className="flex gap-2">
        <select
          value={form.target_country}
          onChange={e => setForm(f => ({ ...f, target_country: e.target.value }))}
          data-testid="business-target-country-select"
          className="flex-1 px-3 py-2.5 text-sm border border-zinc-100 bg-white focus:outline-none focus:ring-2 rounded-lg"
          style={ring}
        >
          <option value="">Not set (Indonesia)</option>
          {options.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
        </select>
        <button type="button" onClick={detect} disabled={detecting}
          className="inline-flex items-center gap-1.5 px-3 text-xs font-bold border border-zinc-200 rounded-lg text-zinc-700 hover:text-zinc-900 hover:border-zinc-300 disabled:opacity-50"
        >
          {detecting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={2} />}
          Detect from Search Console
        </button>
      </div>
      {optionsError && (
        <p className="text-[10px] text-red-500 mt-1">
          Couldn't load the country list from the server. If you just deployed, restart or redeploy the backend.
        </p>
      )}
      {detected && (
        <Hint>
          {detected.breakdown?.length
            ? <>Impressions, last 90 days: {detected.breakdown.map(c => `${c.name} ${c.share_pct}%`).join(" · ")}</>
            : "Couldn't read countries from Search Console. Connect it first, or pick one."}
        </Hint>
      )}
      <Hint>The market you sell to. AI citation checks run in this country's Google, and changing it moves existing checks.</Hint>
    </div>
  );
}

export default function BusinessSection() {
  const { form, handleChange } = useSettings();

  return (
    <SettingsForm>
      <Card icon={Globe} title="Website" subtitle="Required. Everything below is drafted from it, and query analysis matches your pages against it">
        <Field label="Website URL" required type="url" name="astro_site_base_url" value={form.astro_site_base_url} onChange={handleChange}
          placeholder="https://yoursite.com" data-testid="business-website-url-input"
          hint="Your company's main site. Same field as Live Site URL under Site & Sync." />
        <TargetCountryField />
      </Card>

      <Card icon={BookOpen} title="What We Do" subtitle="Grounds AI outlines and query classification in your specific business">
        <DraftFromWebsite />
        <div>
          <Label>Business Context</Label>
          <textarea
            name="business_context" value={form.business_context} onChange={handleChange} rows={3}
            placeholder="e.g. We build D2C AI Agents for WhatsApp Commerce Storefronts that increase conversion rates and repeat orders for e-commerce brands in Southeast Asia."
            className="w-full px-3 py-2.5 text-sm border border-zinc-100 bg-white focus:outline-none focus:ring-2 placeholder-zinc-400 rounded-lg resize-none"
            style={ring}
          />
          <Hint>AI outlines will reference this — no more generic articles that ignore your product.</Hint>
        </div>
        <div>
          <Label>Article Objective</Label>
          <textarea
            name="article_objective" value={form.article_objective} onChange={handleChange} rows={2}
            placeholder="e.g. Educate mid-funnel D2C brand owners comparing WhatsApp automation tools — drive demo signups."
            className="w-full px-3 py-2.5 text-sm border border-zinc-100 bg-white focus:outline-none focus:ring-2 placeholder-zinc-400 rounded-lg resize-none"
            style={ring}
          />
          <Hint>Every outline CTA and section framing will serve this goal.</Hint>
        </div>
      </Card>

      <Card icon={Users} title="Buyer Personas"
        subtitle={'Used by "Score Persona Fit" on each article, and by Query Gap Analysis to judge whether a searcher is one of these buyers'}
      >
        <ListEditor
          list="buyer_personas" blank={{ name: "", description: "" }}
          empty="No personas defined yet — add one to enable persona-fit scoring on articles."
          renderRow={(p, set) => (
            <div className="space-y-1.5">
              <input type="text" value={p.name} onChange={e => set("name", e.target.value)}
                placeholder="Persona name (e.g. D2C Growth Marketer)" className={rowInputCls} style={ring} />
              <textarea value={p.description} onChange={e => set("description", e.target.value)} rows={2}
                placeholder="Pain points, vocabulary, funnel stage — what this persona actually cares about"
                className={`${rowInputCls} resize-none`} style={ring} />
            </div>
          )}
        />
      </Card>

      <Card icon={Target} title="Key Pages"
        subtitle="Query Gap Analysis suggests which key page should rank for each query and weights topics that land on these pages"
      >
        <ListEditor
          list="key_pages" blank={{ url: "", purpose: "" }}
          empty="No key pages yet — add the handful of pages that have to rank."
          renderRow={(p, set) => (
            <div className="space-y-1.5">
              <input type="url" value={p.url} onChange={e => set("url", e.target.value)}
                placeholder="https://yoursite.com/whatsapp-crm" className={`${rowInputCls} font-mono`} style={ring} />
              <input type="text" value={p.purpose} onChange={e => set("purpose", e.target.value)}
                placeholder="What this page covers (e.g. WhatsApp CRM product page for sales teams)" className={rowInputCls} style={ring} />
            </div>
          )}
        />
      </Card>

      <Card icon={Swords} title="Competitors" subtitle="Queries naming a competitor are flagged in the Queries tab (name/domain match)">
        <ListEditor
          list="competitors" blank={{ name: "", domain: "" }}
          empty="No competitors yet."
          renderRow={(c, set) => (
            <div className="flex gap-1.5">
              <input type="text" value={c.name} onChange={e => set("name", e.target.value)}
                placeholder="Name (e.g. Respond.io)" className={rowInputCls} style={ring} />
              <input type="text" value={c.domain} onChange={e => set("domain", e.target.value)}
                placeholder="respond.io" className={`${rowInputCls} font-mono`} style={ring} />
            </div>
          )}
        />
      </Card>
    </SettingsForm>
  );
}
